import React, { useState, useEffect } from 'react';
/* global axios */
import api from '../services/apiService';
import { Link } from 'react-router-dom';  // Importiere Link für die Navigation
import { Helmet } from 'react-helmet';  // Import von React Helmet
import Select from 'react-select';

const ItemList = () => {
  const [items, setItems] = useState([]);  // Zustand für die Artikelliste
  const [loading, setLoading] = useState(true); // Zustand für den Ladeindikator
  const [error, setError] = useState(null); // Zustand für Fehler

  // Nur in der ItemList-Komponente:
  const [filters, setFilters] = useState(() => {
    // vorhandene alten Filter aus der ItemList löschen
    localStorage.removeItem('itemListFilters');
    return {
      name: null,
      productGroup: null,
      motorType: null,
      machineType: null,
      itemType: null,
      deactivated: null,
      foreignArticle: null,
    };
  });

  useEffect(() => {
    localStorage.setItem('itemListFilters', JSON.stringify(filters));
    setCurrentPage(1); // Bei Filteränderungen Seite zurücksetzen
  }, [filters]);

  // State für serverseitige Filteroptionen
  const [serverFilterOptions, setServerFilterOptions] = useState({
    names: [],
    productGroups: [],
    motorTypes: [],
    machineTypes: [],
    itemTypes: [],
    deactivated: [
      { value: true, label: 'Ja' },
      { value: false, label: 'Nein' }
    ],
    foreignArticle: [
      { value: true, label: 'Ja' },
      { value: false, label: 'Nein' }
    ],
  });

  // Filteroptionen über API laden
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await api.get('/items/filter-options');
        setServerFilterOptions(prev => {
          const newOptions = { ...prev, ...response.data };

          // Prüfe, ob der gespeicherte Filterwert für "foreignArticle" noch vorhanden ist
          if (filters.foreignArticle) {
            const found = newOptions.foreignArticle.find(
              option => option.value === filters.foreignArticle.value
            );
            if (!found) {
              // Falls der gespeicherte Wert nicht in den Optionen enthalten ist, setze ihn zurück
              setFilters(current => ({ ...current, foreignArticle: null }));
            }
          }
          return newOptions;
        });
      } catch (error) {
        console.error('Fehler beim Laden der Filteroptionen:', error);
      }
    };
    fetchFilterOptions();
  }, []);


  // Pagination-Zustände
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 25;

  // buildQuery-Funktion, um Query-Parameter basierend auf den Filtern und der Pagination zu erstellen
  const buildQuery = () => {
    const params = new URLSearchParams();
    params.append('limit', limit);
    params.append('page', currentPage);
    if (filters.name) {
      params.append('name', filters.name.value);
    }
    if (filters.productGroup) {
      params.append('productGroup', filters.productGroup.value);
    }
    if (filters.motorType) {
      params.append('motorType', filters.motorType.value);
    }
    if (filters.machineType) {
      params.append('machineType', filters.machineType.value);
    }
    if (filters.itemType) {
      params.append('itemType', filters.itemType.value);
    }
    if (filters.deactivated !== null) {
      params.append('deactivated', filters.deactivated.value);
    }
    if (filters.foreignArticle !== null) {
      params.append('foreignArticle', filters.foreignArticle.value);
    }
    return params.toString() ? `?${params.toString()}` : '';
  };

  // API-Call: Lädt serverseitig gefilterte und paginierte Items
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const query = buildQuery();
        const response = await api.get(`/items/list${query}`);
        // Prüfe, ob die API ein Objekt mit "data" zurückgibt:
        setItems(response.data.data || response.data);
        setTotalPages(response.data.pages);
        setLoading(false);
      } catch (error) {
        console.error("Fehler beim Abrufen der Artikel:", error);
        setError("Fehler beim Abrufen der Artikel");
        setLoading(false);
      }
    };

    fetchItems();
  }, [filters, currentPage]);


  // Ladezustand anzeigen
  if (loading) {
    return <div>Lade Artikel...</div>;
  }

  // Fehlerzustand anzeigen
  if (error) {
    return <div>{error}</div>;
  }

  // Wenn keine Artikel gefunden wurden
  if (items.length === 0) {
    return <div>Keine Artikel gefunden.</div>;
  }

  // Anzeige der Artikelliste
  return (
    <div>
      <Helmet>
        <title>Artikelliste</title>
        <meta name="description" content="Artikelliste" />
      </Helmet>
      <h2>Artikelliste</h2>

      {/* Filterzeile oberhalb der Tabelle */}
      <div style={{ marginBottom: '20px' }}>
        <h3>Filter</h3>
        <div className="filter-grid">
          <Select
            options={serverFilterOptions.names.map(val => ({ value: val, label: val }))}
            placeholder="Name"
            isClearable
            value={filters.name}
            onChange={(selected) => setFilters({ ...filters, name: selected })}
          />
          <Select
            options={serverFilterOptions.productGroups.map(val => ({ value: val, label: val }))}
            placeholder="Produktgruppe"
            isClearable
            value={filters.productGroup}
            onChange={(selected) => setFilters({ ...filters, productGroup: selected })}
          />
          <Select
            options={serverFilterOptions.motorTypes.map(val => ({ value: val, label: val }))}
            placeholder="Motortyp"
            isClearable
            value={filters.motorType}
            onChange={(selected) => setFilters({ ...filters, motorType: selected })}
          />
          <Select
            options={serverFilterOptions.machineTypes.map(val => ({ value: val, label: val }))}
            placeholder="Maschinentyp"
            isClearable
            value={filters.machineType}
            onChange={(selected) => setFilters({ ...filters, machineType: selected })}
          />
          <Select
            options={serverFilterOptions.itemTypes.map(val => ({ value: val, label: val }))}
            placeholder="Artikelart"
            isClearable
            value={filters.itemType}
            onChange={(selected) => setFilters({ ...filters, itemType: selected })}
          />
          <Select
            options={serverFilterOptions.deactivated}
            placeholder="Deaktiviert"
            isClearable
            value={filters.deactivated}
            onChange={(selected) => setFilters({ ...filters, deactivated: selected })}
          />
          <Select
            options={serverFilterOptions.foreignArticle}
            placeholder="Fremdartikel"
            isClearable
            value={filters.foreignArticle}
            onChange={(selected) => setFilters({ ...filters, foreignArticle: selected })}
          />
          {/* ❌ SRR / LO / HP Filter entfernt */}
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Beschreibung</th>
            <th>Produktgruppe</th>
            <th>Motortyp</th>
            <th>Maschinentyp</th>
            <th>Hersteller</th>
            <th>Artikelart</th>
            <th>Kalender</th>
            <th>Deakt</th>
            <th>Fremd</th>
            {/* ❌ SRR / LO / HP Spalten entfernt */}
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>{item.id}</td>
              <td>{item.name}</td>
              <td>{item.description || '-'}</td>
              <td>{item.productGroup?.name || '-'}</td>
              <td>{item.motorType?.name || '-'}</td>
              <td>{item.machineType?.name || '-'}</td>
              <td>{item.manufacturer || '-'}</td>
              <td>{item.itemType || '-'}</td>
              <td>{item.visibleInCalendar ? 'Ja' : 'Nein'}</td>
              <td>{item.deactivated ? 'Ja' : 'Nein'}</td>
              <td>{item.foreignArticle ? 'Ja' : 'Nein'}</td>
              <td>
                <Link to={`/itemform/${item.id}`}>Bearbeiten</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      <div style={{ marginTop: '1rem' }}>
        <button
          onClick={() => setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)}
          disabled={currentPage === 1}
          style={{
            fontSize: '15px',
            padding: '8px 12px',
            backgroundColor: 'rgb(155, 155, 155)',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Vorherige
        </button>
        <span style={{ margin: '0 1rem' }}>
          Seite {currentPage} von {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage >= totalPages}
          style={{
            fontSize: '15px',
            padding: '8px 12px',
            backgroundColor: 'rgb(155, 155, 155)',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Nächste
        </button>
      </div>
    </div>
  );
};

export default ItemList;
